/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Glycophil Landing Page Logic
   Path: assets/gp-landing.js
   Used by: all gp-landing-{product-family}-{argument}-{version}.liquid sections

   Handles:
   - Product image gallery (thumb click → swap main image)
   - Recogniser (3-question skin context diagnostic)
   - Sticky cart visibility on scroll
   - Add to Cart submission via Shopify's /cart/add.js API
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

(function () {
  'use strict';

  const root = document.querySelector('.landing-page');
  if (!root) return;

  // ═══════════════════════════════════════════════════════════
  // PRODUCT IMAGE GALLERY
  // ═══════════════════════════════════════════════════════════
  const galleryMain = root.querySelector('.hero-gallery-main');
  const heroMainImage = root.querySelector('#hero-main-image');
  const heroThumbs = root.querySelectorAll('.hero-gallery-thumb');

  if (heroMainImage && heroThumbs.length > 0) {
    heroThumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        const newSrc = thumb.dataset.image;
        if (!newSrc || thumb.classList.contains('active')) return;

        // Update active state
        heroThumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');

        // Fade out, swap, fade in
        if (galleryMain) galleryMain.classList.add('swapping');
        const newImg = new Image();
        newImg.onload = function () {
          heroMainImage.src = newSrc;
          if (galleryMain) galleryMain.classList.remove('swapping');
        };
        newImg.onerror = function () {
          if (galleryMain) galleryMain.classList.remove('swapping');
        };
        newImg.src = newSrc;
      });
    });

    // Keyboard navigation (left/right arrows when gallery is focused)
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const active = root.querySelector('.hero-gallery-thumb.active');
      if (!active) return;
      const thumbs = Array.from(heroThumbs);
      const currentIndex = thumbs.indexOf(active);
      let nextIndex;
      if (e.key === 'ArrowLeft') {
        nextIndex = currentIndex === 0 ? thumbs.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex === thumbs.length - 1 ? 0 : currentIndex + 1;
      }
      thumbs[nextIndex].click();
    });
  }

  // ═══════════════════════════════════════════════════════════
  // RECOGNISER
  // ═══════════════════════════════════════════════════════════
  const questions = root.querySelectorAll('.question');
  const resultBox = root.querySelector('#recogniser-result');
  const resultTitle = root.querySelector('#result-title');
  const resultText = root.querySelector('#result-text');
  const answers = {};

  const results = {
    high: {
      title: 'Your skin is likely making oil and losing water at the same time.',
      text: 'The oil you see is real. The dehydration underneath is real. Both need to be addressed — not separately, but together as one system. Adaptive Pack is built for exactly this state.'
    },
    mid: {
      title: 'Your skin is showing early signs that its barrier is starting to wear down.',
      text: 'The signals come and go — usually tied to weather shifts, new products, or a routine that keeps changing. The fix is to stabilise the barrier first. The surface symptoms usually clear up over a few weeks once the barrier is back to normal.'
    },
    low: {
      title: 'Your skin is in a balanced state right now.',
      text: 'Skin Context shifts — what\'s balanced this week can change in a few weeks. Adaptive Pack is built to support stable skin as much as compromised skin, so the routine works regardless of where your skin is on any given day.'
    }
  };

  questions.forEach(function (q) {
    const options = q.querySelectorAll('.option');
    options.forEach(function (opt) {
      opt.addEventListener('click', function () {
        options.forEach(function (o) { o.classList.remove('selected'); });
        opt.classList.add('selected');
        answers[q.dataset.q] = parseInt(opt.dataset.score, 10);
        checkComplete();
      });
    });
  });

  function checkComplete() {
    if (Object.keys(answers).length !== 3) return;
    const total = Object.values(answers).reduce(function (a, b) { return a + b; }, 0);

    let key;
    if (total >= 4) key = 'high';
    else if (total >= 2) key = 'mid';
    else key = 'low';

    resultTitle.textContent = results[key].title;
    resultText.textContent = results[key].text;
    resultBox.classList.add('show');
    setTimeout(function () {
      resultBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  // ═══════════════════════════════════════════════════════════
  // STICKY CART VISIBILITY
  // ═══════════════════════════════════════════════════════════
  const stickyCart = root.querySelector('#sticky-cart');
  const hero = root.querySelector('.hero');

  if (stickyCart && hero) {
    function handleScroll() {
      const heroBottom = hero.offsetTop + hero.offsetHeight;
      if (window.scrollY > heroBottom) {
        stickyCart.classList.add('visible');
      } else {
        stickyCart.classList.remove('visible');
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  // ═══════════════════════════════════════════════════════════
  // ADD TO CART
  // Handles all forms with data-cart-form attribute (hero, pack-cta, sticky)
  // ═══════════════════════════════════════════════════════════
  const cartForms = root.querySelectorAll('[data-cart-form]');

  cartForms.forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const submitButton = form.querySelector('[data-cart-submit]');
      const textSpan = submitButton.querySelector('.cta-text');
      const loadingSpan = submitButton.querySelector('.cta-loading');

      submitButton.disabled = true;
      if (textSpan) textSpan.hidden = true;
      if (loadingSpan) loadingSpan.hidden = false;

      const formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
      .then(function (response) {
        if (!response.ok) throw new Error('Add to cart failed');
        return response.json();
      })
      .then(function () {
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.error('Cart error:', err);
        submitButton.disabled = false;
        if (textSpan) textSpan.hidden = false;
        if (loadingSpan) loadingSpan.hidden = true;
        alert('Could not add to cart. Please try again.');
      });
    });
  });

})();